import {
  chartColorScale,
  emailReportColorScale,
  lineChartFactory,
  formattedAverageTime,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import {
  type GoalCards,
  type GoalCardsSharepointFileShares,
  type GoalCardsSharepointFileSyncs,
  type GoalCardsSharepointFileViews,
  type GoalCardsSharepointPageVisits,
  type GoalCardsTeamsMeetings,
  type GoalCardsTeamsMessages,
  type GoalLineChart,
  type GoalTotalCopilot,
  type GoalTotalSharepointFileShares,
  type GoalTotalSharepointFileSyncs,
  type GoalTotalSharepointFileViews,
  type GoalTotalSharepointPageVisits,
  type GoalTotalTeamsMeetings,
  type GoalTotalTeamsMessages,
  type GoalTotals,
} from "../../types/performance";
import { type HorizontalBarProps, type LinechartProps, getDateFormatByCount } from "components/charts";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import { isEmpty } from "lodash";
import { type ValidFlowGoalHeader } from "../slices/flowPerformanceSlice";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";
import { COPILOT_APP_COLOR } from "features/Reporting/MsGraph/Copilot/constants";

export const orgChartTooltipFormatter = (flowAverage: number, orgAverage: number) =>
  `Flow Average: ${roundToTwoDigits(flowAverage)}\nOrg. Average: ${roundToTwoDigits(orgAverage)}`;

const lineTooltipFormatter = (_x: unknown, y: any, _yIndex: number, _xIndex: number, d: any) =>
  orgChartTooltipFormatter(y, d);

const emptyLineInfo: LinechartProps = { xData: [], yData: [] };

export const getGoalLineChart = (
  prepackedLineData: GoalLineChart | undefined,
  flowHeader: ValidFlowGoalHeader,
): LinechartProps => {
  if (flowHeader.toLowerCase().includes("copilot")) {
    return getCopilotAllActivityLine(prepackedLineData);
  }

  switch (flowHeader) {
    case "Teams Meetings":
      return getTeamsMeetingsLineChart(prepackedLineData);
    case "Teams Messages":
      return getTeamsMessagesLineChart(prepackedLineData);
    case "Teams Calls":
      return getTeamsCallsLineChart(prepackedLineData);
    case "OneDrive Files Viewed":
      return getOnedriveFilesViewedLineChart(prepackedLineData);
    case "OneDrive Files Shared":
      return getOnedriveFilesSharedLineChart(prepackedLineData);
    case "OneDrive Files Synced":
      return getOnedriveFilesSyncedLineChart(prepackedLineData);
    case "SharePoint Files Synced":
      return getSharepointFilesSyncedLine(prepackedLineData);
    case "SharePoint Files Viewed":
      return getSharepointFilesViewedLine(prepackedLineData);
    case "SharePoint Page Visits":
      return getSharepointPageVisitsLine(prepackedLineData);
    case "SharePoint Shared Files":
      return getSharepointSharedFilesLine(prepackedLineData);
    default:
      return emptyLineInfo;
  }
};

// Engagement
export const getGoalEngagementChart = (value: GoalTotals, flowGoalInfo: any): HorizontalBarProps => {
  // Teams meetings
  if ("AdHocMeetingsAttendedCountFlow" in value) {
    return getTeamsMeetingsEngagementChart(value);
  }
  // Teams messages
  else if ("PostMessagesFlow" in value) {
    return getTeamsMessagesEngagementChart(value);
  } else if ("CallCountFlow" in value) {
    const data = [
      {
        id: "1",
        value: value.CallCountFlow,
        category: "Calls",
        fillColor: reportingColors.blue,
      },
    ];

    return {
      data,
      tooltipFormatter: () => {
        return orgChartTooltipFormatter(value.CallCountFlow, value.CallCountOrg);
      },
    };
  } else if ("SharedExternallyFileCountFlow" in value) {
    if (flowGoalInfo.applicationLabel === "SharePoint") {
      return getSharepointSharedFilesEngagement(value);
    }
    const data = [
      {
        id: "1",
        value: value.SharedInternallyFileCountFlow,
        category: "Internal",
        fillColor: reportingColors.blue,
      },
      {
        id: "2",
        value: value.SharedExternallyFileCountFlow,
        category: "External",
        fillColor: reportingColors.yellow,
      },
    ];

    return {
      data,
      tooltipFormatter: (d) => {
        switch (d.id) {
          case "1":
            return orgChartTooltipFormatter(value.SharedInternallyFileCountFlow, value.SharedInternallyFileCountOrg);
          case "2":
          default:
            return orgChartTooltipFormatter(value.SharedExternallyFileCountFlow, value.SharedExternallyFileCountOrg);
        }
      },
    };
    // OneDrive Files viewed | Sharepoint Files viewed
  } else if ("ViewedOrEditedFileCountFlow" in value) {
    if (flowGoalInfo?.applicationLabel === "SharePoint") {
      return getSharepointFilesViewedEngagement(value);
    }
    const data = [
      {
        id: "1",
        value: value.ViewedOrEditedFileCountFlow,
        category: "Files Viewed or Edited",
        fillColor: reportingColors.blue,
      },
    ];

    return {
      data,
      tooltipFormatter: () =>
        orgChartTooltipFormatter(value.ViewedOrEditedFileCountFlow, value.ViewedOrEditedFileCountOrg),
    };
  }
  // OneDrive Syncs | Sharepoint Syncs
  else if ("SyncedFileCountFlow" in value) {
    if (flowGoalInfo?.applicationLabel === "SharePoint") {
      return getSharepointFilesSyncedEngagement(value);
    }
    const data = [
      {
        id: "1",
        value: value.SyncedFileCountFlow,
        category: "Files synced",
        fillColor: reportingColors.blue,
      },
    ];

    return {
      data,
      tooltipFormatter: () => orgChartTooltipFormatter(value.SyncedFileCountFlow, value.SyncedFileCountOrg),
    };
  } else if ("VisitedPageCountFlow" in value) {
    return getSharepointPageVisitsEngagement(value);
  } else if ("TeamsPctFlow" in value) {
    return getCopilotEngagement(value);
  }

  return { data: [] };
};

export const getGoalCards = (value: GoalCards, flowGoalInfo: any): CardReportingItem[] => {
  // Teams Meetings
  if ("AvgMeetingOrganized" in value) {
    return getTeamsMeetingsCards(value);
  }
  // Teams messages
  else if ("AvgChats" in value) {
    return getTeamsMessagesCards(value);
    // Teams calls
  } else if ("AvgCallDuration" in value) {
    return [
      {
        stat: value.TotalCallCount,
        popupBody: "Total number of 1:1 calls that users participated in during the specified time period.",
        statDescription: "Calls",
      },
      {
        stat: value.AvgCalls,
        popupBody:
          "Total number of 1:1 calls divided by the total number of participating users during the specified time period.",
        statDescription: "Avg. Calls/Person",
      },
      {
        stat: formattedAverageTime(value.AvgCallDuration),
        popupBody:
          "The total call audio durations divided by the total number of participating users during the specified time period.",
        statDescription: "Avg. Call Duration",
      },
    ];
  }
  // OneDrive shared | Sharepoint shared
  else if ("AvgExternalShares" in value) {
    if (flowGoalInfo?.applicationLabel === "SharePoint") {
      return getSharepointSharedFilesCards(value);
    }
    // OneDrive shared
    return [
      {
        stat: value.TotalSharedFileCount,
        popupBody:
          "The total number of files that have been shared with users outside or within the organization, or with users within groups (that might include external users).",
        statDescription: "Files Shared",
      },
      {
        stat: value.AvgInternalShares,
        popupBody:
          "The number of files that have been shared with users within the organization, or with users within groups, divided by the total number of participating users during the specified time period.",
        statDescription: "Avg. Internal Shares/Person",
      },
      {
        stat: value.AvgExternalShares,
        popupBody:
          "The number of files that have been shared with users outside of the organization divided by the total number of participating users during the specified time period.",
        statDescription: "Avg. External Shares/Person",
      },
    ];
  }
  // OneDrive files viewed or Sharepoint files viewed
  else if ("AvgViewedOrEditedFileCount" in value) {
    if (flowGoalInfo?.applicationLabel === "SharePoint") {
      return getSharepointFilesViewedCards(value);
    }
    // OneDrive viewed
    return [
      {
        stat: value.ViewedOrEditedFileCount,
        popupBody: "The number of files that the user uploaded, downloaded, modified, or viewed.",
        statDescription: "Files Viewed or Edited",
      },
      {
        stat: value.AvgViewedOrEditedFileCount,
        popupBody:
          "The total number of files viewed or edited divided by the total number of participating users during the specified time period.",
        statDescription: "Avg. Files Viewed or Edited/Person",
      },
    ];
  } else if ("AvgFileSynced" in value) {
    if (flowGoalInfo?.applicationLabel === "SharePoint") {
      return getSharepointFilesSyncedCards(value);
    }
    return [
      {
        stat: value.TotalSyncedFileCount,
        popupBody: "The total number of files that have been synced from user's local devices to the OneDrive account.",
        statDescription: "Files Synced",
      },
      {
        stat: value.AvgFileSynced,
        popupBody:
          "The total number of files synced divided by the total number of participating users during the specified time period.",
        statDescription: "Avg. Files Synced/Person",
      },
    ];
  } else if ("VisitedPageCount" in value) {
    return getSharepointPageVisitCards(value);
  }

  return [];
};

// Teams Meetings
const getTeamsMeetingsLineChart = (prepackedLineData: GoalLineChart | undefined): LinechartProps => {
  if (!prepackedLineData) return { xData: [], yData: [] };
  const lineData = lineChartFactory(prepackedLineData.map((l) => ({ ...l, ...l?.Events })));
  if (isEmpty(lineData)) {
    return { xData: [], yData: [] };
  }

  return {
    xData: [lineData.Date, lineData.Date],
    yData: [lineData.MeetingsAttendedCountFlow, lineData.MeetingsOrganizedCountFlow],
    dashedYData: [lineData.MeetingsAttendedCountOrg, lineData.MeetingsOrganizedCountOrg],
    xFormatterFunc: getDateFormatByCount(lineData.Date.length),
    colorRange: chartColorScale,
    tooltipFormatter: lineTooltipFormatter,
  };
};

const getTeamsMeetingsEngagementChart = (value: GoalTotalTeamsMeetings): HorizontalBarProps => {
  const data = [
    {
      id: "1",
      value: value.AdHocMeetingsAttendedCountFlow,
      category: "Ad hoc attended",
      fillColor: reportingColors.blue,
    },
    {
      id: "2",
      value: value.AdHocMeetingsOrganizedCountFlow,
      category: "Ad hoc organized",
      fillColor: reportingColors.greenDark,
    },
    {
      id: "3",
      value: value.ScheduledOneTimeMeetingsAttendedCountFlow,
      category: "Scheduled one time attended",
      fillColor: reportingColors.yellow,
    },
    {
      id: "4",
      value: value.ScheduledRecurringMeetingsAttendedCountFlow,
      category: "Scheduled recurring attended",
      fillColor: reportingColors.red,
    },
  ];
  return {
    data,
    tooltipFormatter: (d) => {
      switch (d.id) {
        case "1":
          return orgChartTooltipFormatter(value.AdHocMeetingsAttendedCountFlow, value.AdHocMeetingsAttendedCountOrg);
        case "2":
          return orgChartTooltipFormatter(value.AdHocMeetingsOrganizedCountFlow, value.AdHocMeetingsOrganizedCountOrg);
        case "3":
          return orgChartTooltipFormatter(
            value.ScheduledOneTimeMeetingsAttendedCountFlow,
            value.ScheduledOneTimeMeetingsAttendedCountOrg,
          );
        case "4":
        default:
          return orgChartTooltipFormatter(
            value.ScheduledRecurringMeetingsAttendedCountFlow,
            value.ScheduledRecurringMeetingsAttendedCountOrg,
          );
      }
    },
  };
};

const getTeamsMeetingsCards = (value: GoalCardsTeamsMeetings): CardReportingItem[] => [
  {
    stat: value.TotalParticipatedMeetings,
    popupBody:
      "The sum of one-time scheduled, recurring, ad hoc and unclassified meetings participated in during the specified time period.",
    statDescription: "Participated Meetings",
  },
  {
    stat: value.AvgMeetingParticipated,
    popupBody:
      "Total participated meetings divided by the total number of participating users during the specified time period.",
    statDescription: "Avg. Participated Meetings/Person",
  },
  {
    stat: value.TotalOrganizedMeetings,
    popupBody:
      "The sum of one-time scheduled, recurring, ad hoc and unclassified meetings organized during the specified time period.",
    statDescription: "Organized Meetings",
  },
  {
    stat: value.AvgMeetingOrganized,
    popupBody:
      "Total organized meetings divided by the total number of participating users during the specified time period.",
    statDescription: "Avg. Organized Meetings/Person",
  },
];

// Teams messages
const getTeamsMessagesLineChart = (prepackedLineData: GoalLineChart | undefined): LinechartProps => {
  if (!prepackedLineData) return { xData: [], yData: [] };
  const lineData = lineChartFactory(prepackedLineData.map((l) => ({ ...l, ...l?.Events })));
  if (isEmpty(lineData)) {
    return { xData: [], yData: [] };
  }

  return {
    xData: [lineData.Date, lineData.Date, lineData.Date],
    yData: [lineData.PostMessagesFlow, lineData.PrivateChatMessageCountFlow, lineData.ReplyMessagesCountFlow],
    dashedYData: [lineData.PostMessagesOrg, lineData.PrivateChatMessageCountOrg, lineData.ReplyMessagesCountOrg],
    xFormatterFunc: getDateFormatByCount(lineData.Date.length),
    colorRange: emailReportColorScale,
  };
};

const getTeamsMessagesEngagementChart = (value: GoalTotalTeamsMessages): HorizontalBarProps => {
  const data = [
    {
      id: "1",
      value: value.PostMessagesFlow,
      category: "Posts",
      fillColor: reportingColors.blue,
    },
    {
      id: "2",
      value: value.PrivateChatMessageCountFlow,
      category: "Chats",
      fillColor: reportingColors.greenDark,
    },
    {
      id: "3",
      value: value.ReplyMessagesCountFlow,
      category: "Replies",
      fillColor: reportingColors.yellow,
    },
  ];
  return {
    data,
    tooltipFormatter: (d) => {
      switch (d.id) {
        case "1":
          return orgChartTooltipFormatter(value.PostMessagesFlow, value.PostMessagesOrg);
        case "2":
          return orgChartTooltipFormatter(value.PrivateChatMessageCountFlow, value.PrivateChatMessageCountOrg);
        case "3":
        default:
          return orgChartTooltipFormatter(value.ReplyMessagesCountFlow, value.ReplyMessagesCountOrg);
      }
    },
  };
};

const getTeamsMessagesCards = (value: GoalCardsTeamsMessages): CardReportingItem[] => [
  {
    stat: value.AvgPosts,
    popupBody:
      "Total number of post messages in all channels divided by the total number of participating users during the specified time period.",
    statDescription: "Avg. Posts/Person",
  },
  {
    stat: value.AvgChats,
    popupBody:
      "Total number of unique messages that users posted in a private chat divided by the total number of participating users during the specified time period.",
    statDescription: "Avg. Chats/Person",
  },
  {
    stat: value.AvgReplies,
    popupBody:
      "Total number of replied messages in all channels divided by the total number of participating users during the specified time period.",
    statDescription: "Avg. Replies/Person",
  },
];

// Teams Cards
const getTeamsCallsLineChart = (prepackedLineData: GoalLineChart | undefined): LinechartProps => {
  if (!prepackedLineData) return { xData: [], yData: [] };
  const lineData = lineChartFactory(prepackedLineData.map((l) => ({ ...l, ...l?.Events })));
  if (isEmpty(lineData)) {
    return { xData: [], yData: [] };
  }
  return {
    xData: [lineData.Date],
    yData: [lineData.CallCountFlow],
    dashedYData: [lineData.CallCountOrg],
    xFormatterFunc: getDateFormatByCount(lineData.Date.length),
    colorRange: [reportingColors.blue],
    tooltipFormatter: lineTooltipFormatter,
  };
};

const getOnedriveFilesViewedLineChart = (prepackedLineData: GoalLineChart | undefined) => {
  if (!prepackedLineData) return { xData: [], yData: [] };

  const lineData = lineChartFactory(prepackedLineData.map((l) => ({ ...l, ...l?.Events })));
  if (isEmpty(lineData)) {
    return { xData: [], yData: [] };
  }

  return {
    xData: [lineData.Date],
    yData: [lineData.ViewedOrEditedFileCountFlow],
    dashedYData: [lineData.ViewedOrEditedFileCountOrg],
    xFormatterFunc: getDateFormatByCount(lineData.Date.length),
    colorRange: [reportingColors.blue],
    tooltipFormatter: lineTooltipFormatter,
  };
};

const getOnedriveFilesSharedLineChart = (prepackedLineData: GoalLineChart | undefined) => {
  if (!prepackedLineData) return { xData: [], yData: [] };

  const lineData = lineChartFactory(prepackedLineData.map((l) => ({ ...l, ...l?.Events })));
  if (isEmpty(lineData)) {
    return { xData: [], yData: [] };
  }

  return {
    xData: [lineData.Date, lineData.Date],
    yData: [lineData.SharedInternallyFileCountFlow, lineData.SharedExternallyFileCountFlow],
    dashedYData: [lineData.SharedInternallyFileCountOrg, lineData.SharedExternallyFileCountOrg],
    xFormatterFunc: getDateFormatByCount(lineData.Date.length),
    colorRange: chartColorScale,
    tooltipFormatter: lineTooltipFormatter,
  };
};

const getOnedriveFilesSyncedLineChart = (prepackedLineData: GoalLineChart | undefined) => {
  if (!prepackedLineData) return { xData: [], yData: [] };

  const lineData = lineChartFactory(prepackedLineData.map((l) => ({ ...l, ...l?.Events })));
  if (isEmpty(lineData)) {
    return { xData: [], yData: [] };
  }

  return {
    xData: [lineData.Date],
    yData: [lineData.SyncedFileCountFlow],
    dashedYData: [lineData.SyncedFileCountOrg],
    xFormatterFunc: getDateFormatByCount(lineData.Date.length),
    colorRange: [reportingColors.blue],
    tooltipFormatter: lineTooltipFormatter,
  };
};

// Copilot
const getCopilotAllActivityLine = (prepackedLineData: GoalLineChart | undefined): LinechartProps => {
  if (!prepackedLineData) return { xData: [], yData: [] };

  const lineData = lineChartFactory(prepackedLineData.map((l) => ({ ...l, ...l?.Events })));
  if (isEmpty(lineData)) {
    return { xData: [], yData: [] };
  }

  return {
    xData: [lineData.Date],
    yData: [lineData.ActiveUserCountFlow],
    dashedYData: [lineData.ActiveUserCountOrg],
    xFormatterFunc: getDateFormatByCount(lineData.Date.length),
    colorRange: [reportingColors.blue],
  };
};

// For now, these behave the same
const getSharepointFilesSyncedLine = getOnedriveFilesSyncedLineChart;
const getSharepointFilesViewedLine = getOnedriveFilesViewedLineChart;

const getSharepointPageVisitsLine = (prepackedLineData: GoalLineChart | undefined): LinechartProps => {
  if (!prepackedLineData) return { xData: [], yData: [] };

  const lineData = lineChartFactory(prepackedLineData.map((l) => ({ ...l, ...l?.Events })));
  if (isEmpty(lineData)) {
    return { xData: [], yData: [] };
  }

  return {
    xData: [lineData.Date],
    yData: [lineData.VisitedPageCountFlow],
    dashedYData: [lineData.VisitedPageCountOrg],
    xFormatterFunc: getDateFormatByCount(lineData.Date.length),
    colorRange: [reportingColors.blue],
    tooltipFormatter: lineTooltipFormatter,
  };
};

// NOTE: For now, these behave the same but that may change in the future so I will define it separately
const getSharepointSharedFilesLine = getOnedriveFilesSharedLineChart;

const getSharepointFilesSyncedEngagement = (value: GoalTotalSharepointFileSyncs): HorizontalBarProps => {
  const data: DataPoint[] = [
    {
      id: "1",
      value: value.SyncedFileCountFlow,
      category: "Synced",
      fillColor: reportingColors.blue,
    },
  ];
  return {
    data,
    tooltipFormatter: () => orgChartTooltipFormatter(value.SyncedFileCountFlow, value.SyncedFileCountOrg),
  };
};

const getSharepointFilesViewedEngagement = (value: GoalTotalSharepointFileViews): HorizontalBarProps => {
  const data = [
    {
      id: "1",
      value: value.ViewedOrEditedFileCountFlow,
      category: "Viewed",
      fillColor: reportingColors.blue,
    },
  ];
  return {
    data,
    tooltipFormatter: () =>
      orgChartTooltipFormatter(value.ViewedOrEditedFileCountFlow, value.ViewedOrEditedFileCountOrg),
  };
};

const getSharepointPageVisitsEngagement = (value: GoalTotalSharepointPageVisits): HorizontalBarProps => {
  const data = [
    {
      id: "1",
      value: value.VisitedPageCountFlow,
      category: "Visits",
      fillColor: reportingColors.blue,
    },
  ];
  return {
    data,
    tooltipFormatter: () => orgChartTooltipFormatter(value.VisitedPageCountFlow, value.VisitedPageCountOrg),
  };
};

const getSharepointSharedFilesEngagement = (value: GoalTotalSharepointFileShares): HorizontalBarProps => {
  const data = [
    {
      id: "1",
      value: value.SharedInternallyFileCountFlow,
      category: "Internal",
      fillColor: reportingColors.blue,
    },
    {
      id: "2",
      value: value.SharedExternallyFileCountFlow,
      category: "External",
      fillColor: reportingColors.yellow,
    },
  ];

  return {
    data,
    tooltipFormatter: (d) => {
      if (d.id === "1") {
        return orgChartTooltipFormatter(value.SharedInternallyFileCountFlow, value.SharedExternallyFileCountOrg);
      } else {
        return orgChartTooltipFormatter(value.SharedExternallyFileCountFlow, value.SharedExternallyFileCountOrg);
      }
    },
  };
};

const getCopilotEngagement = (value: GoalTotalCopilot): HorizontalBarProps => {
  const data = [
    {
      id: "teams",
      value: value.TeamsPctFlow,
      category: "Teams",
      fillColor: COPILOT_APP_COLOR.copilot_msft_teams,
    },
    {
      id: "word",
      value: value.WordPctFlow,
      category: "Word",
      fillColor: COPILOT_APP_COLOR.copilot_word,
    },
    {
      id: "excel",
      value: value.ExcelPctFlow,
      category: "Excel",
      fillColor: COPILOT_APP_COLOR.copilot_excel,
    },
    {
      id: "powerpoint",
      value: value.PowerPointPctFlow,
      category: "PowerPoint",
      fillColor: COPILOT_APP_COLOR.copilot_power_point,
    },
    {
      id: "outlook",
      value: value.OutlookPctFlow,
      category: "Outlook",
      fillColor: COPILOT_APP_COLOR.copilot_outlook,
    },
    {
      id: "onenote",
      value: value.OneNotePctFlow,
      category: "OneNote",
      fillColor: COPILOT_APP_COLOR.copilot_one_note,
    },
    {
      id: "loop",
      value: value.LoopPctFlow,
      category: "Loop",
      fillColor: COPILOT_APP_COLOR.copilot_loop,
    },
  ];

  return {
    data,
    tooltipFormatter: (d) => {
      const flowValue = d.value;
      let orgValue: number;
      switch (d.id) {
        case "teams":
          orgValue = value.TeamsPctOrg;
          break;
        case "word":
          orgValue = value.WordPctOrg;
          break;
        case "excel":
          orgValue = value.ExcelPctOrg;
          break;
        case "powerpoint":
          orgValue = value.PowerPointPctOrg;
          break;
        case "outlook":
          orgValue = value.OutlookPctOrg;
          break;
        case "onenote":
          orgValue = value.OneNotePctOrg;
          break;
        case "loop":
        default:
          orgValue = value.LoopPctOrg;
          break;
      }
      return `Flow Percent: ${roundToTwoDigits(flowValue)}\nOrg. Percent: ${roundToTwoDigits(orgValue)}`;
    },
  };
};

const getSharepointFilesSyncedCards = (value: GoalCardsSharepointFileSyncs): CardReportingItem[] => [
  {
    stat: value.TotalSyncedFileCount,
    popupBody: "The number of files that have been synced from a user's local device to the SharePoint site.",
    statDescription: "Files Synced",
  },
  {
    stat: value.AvgFileSynced,
    popupBody:
      "The total number of files synced divided by the total number of participating users during the specified time period.",
    statDescription: "Avg. Files Synced/Person",
  },
];

const getSharepointFilesViewedCards = (value: GoalCardsSharepointFileViews): CardReportingItem[] => [
  {
    stat: value.ViewedOrEditedFileCount,
    popupBody: "The number of files that the user uploaded, downloaded, modified, or viewed.",
    statDescription: "Files Viewed or Edited",
  },
  {
    stat: value.AvgViewedOrEditedFileCount,
    popupBody:
      "The total number of files viewed or edited divided by the total number of participating users during the specified time period.",
    statDescription: "Avg. Files Viewed or Edited/Person",
  },
];

const getSharepointPageVisitCards = (value: GoalCardsSharepointPageVisits): CardReportingItem[] => [
  {
    stat: value.VisitedPageCount,
    popupBody: "The total number of visits to unique pages by the users.",
    statDescription: "Pages Visited",
  },
  {
    stat: value.AvgVisitedPageCount,
    popupBody:
      "The total number of pages visited divided by the total number of participating users during the specified time period.",
    statDescription: "Avg. Pages Visited/Person",
  },
];

const getSharepointSharedFilesCards = (value: GoalCardsSharepointFileShares): CardReportingItem[] => [
  {
    stat: value.TotalSharedFileCount,
    popupBody:
      "The total number of files shared with users inside or outside the organization, including group members.",
    statDescription: "Files Shared",
  },
  {
    stat: value.AvgInternalShares,
    popupBody:
      "The total number of files shared internally divided by the total number of participating users during the specified time period.",
    statDescription: "Avg. Internal Shares/Person",
  },
  {
    stat: value.AvgExternalShares,
    popupBody:
      "The total number of files shared externally divided by the total number of participating users during the specified time period.",
    statDescription: "Avg. External Shares/Person",
  },
];
